import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpContext } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';

import { environment } from '@env/environment';

import { AuthQuery } from '@app/auth/auth.query';
import { SILENT_ERROR } from '@app/@core/http/error-handler.interceptor';

export interface EventDay {
  day_no: number;
  enabled: number;
  caption: string;
  event_date: string | null;
  slot_start: string | null;
  slot_end: string | null;
  slot_interval: number;
  breaks: string;
  break_caption: string;
  spots: number;
}

export interface ContentBlock {
  title: string;
  html: string;
}

/** A single agenda row within a program day. */
export interface AgendaItem {
  time: string;
  text: string;
  /** Full-width section label row (e.g. "Facultatif:") instead of time + text. */
  label?: boolean;
}

export interface ProgramTab {
  title: string;
  /** Day heading shown above the agenda list (e.g. "Wednesday, 25 September"). */
  heading?: string;
  /** Structured agenda rows. New content uses this. */
  items?: AgendaItem[];
  /** Legacy free-form HTML body; kept for reading older stored content. */
  html?: string;
}

/** block_key -> lang -> { title, html } */
export interface ContentResponse {
  status: number;
  blocks: { [blockKey: string]: { [lang: string]: ContentBlock } };
}

export interface SaveContentBlock {
  block_key: string;
  lang: string;
  title: string;
  html: string;
}

export interface EmailTemplate {
  subject: string;
  body_html: string;
}

/** template_key -> lang -> { subject, body_html } */
export interface EmailTemplatesResponse {
  status: number;
  templates: { [templateKey: string]: { [lang: string]: EmailTemplate } };
}

export interface SaveEmailTemplate {
  template_key: string;
  lang: string;
  subject: string;
  body_html: string;
}

export interface ConfigFileMeta {
  id: string;
  label: string;
  type: 'json' | 'php' | 'env';
  readonly: boolean;
  size: number;
  mtime: number;
}

export interface ConfigFileContent {
  status: number;
  id: string;
  type: string;
  readonly: boolean;
  content: string;
}

export interface ContentImage {
  /** path relative to _fileDB, e.g. "images/_elems/foo.png" */
  path: string;
  name: string;
  size: number;
  modified: number;
}

export interface ContentImagesResponse {
  status: number;
  images: ContentImage[];
}

/** lang -> ikey -> value */
export interface I18nResponse {
  status: number;
  i18n: { [lang: string]: { [ikey: string]: string } };
}

export interface SaveI18nEntry {
  lang: string;
  ikey: string;
  value: string;
}

export interface GalleryImage {
  /** path relative to _fileDB, e.g. "images/gallery/marseille/foo.jpg" */
  path: string;
  /** thumbnail path relative to _fileDB; equals `path` when no thumb exists */
  thumb?: string;
  name: string;
  category: string;
  size: number;
  modified: number;
}

export interface GalleryResponse {
  status: number;
  categories: string[];
  images: GalleryImage[];
}

/** Editable metadata for one gallery category (album). */
export interface GalleryCategoryMeta {
  slug: string;
  title: string;
  description: string;
  caption: string;
  /** cover image path relative to _fileDB; empty = use newest image */
  cover: string;
  order: number;
}

/** The full editable gallery metadata persisted to config/event/gallery.json. */
export interface GalleryMeta {
  /** main hero image path relative to _fileDB */
  hero: string;
  /** intro HTML shown above the album cards */
  intro: string;
  categories: GalleryCategoryMeta[];
}

export interface GalleryMetaResponse {
  status: number;
  meta: GalleryMeta;
}

/** A backend admin user (t_beuser, beusergroup_id = 1). */
export interface AdminUser {
  id: number;
  name: string;
  login: string;
  email: string;
  company: string;
  active: number;
  isapproved: number;
  /** read-only: 1 = superuser. Such rows are shown but not editable here. */
  su: number;
  /** bare filename stored under images/admin (empty when none) */
  profile_image: string;
  lastlogin: string;
  created: string;
}

/** Payload for creating (no id) or updating (with id) an admin user. */
export interface SaveAdminUserPayload {
  id?: number;
  name?: string;
  login?: string;
  email?: string;
  company?: string;
  /** plaintext; hashed (md5) server-side. Omit/empty to keep current on update. */
  password?: string;
  active?: number;
}

export interface EventConfigResponse {
  status: number;
  client: string;
  settings: { [key: string]: any };
  days: EventDay[];
  defaults: {
    event: { title: string; year: string; www_url: string; email: string; frontend_url: string };
    logistics: { hotel: string; event_space: string; event_date: string; book_night: string; cancel_date: string; day1: string; day2: string };
  };
}

/** Read-only system snapshot returned by GET /v3/config/sysinfo (secrets masked). */
export interface SysInfoResponse {
  status: number;
  php: {
    version: string;
    sapi: string;
    os: string;
    opcache_enabled: boolean;
    server_time: string;
    timezone: string;
  };
  paths: {
    api_root: string;
    deploy_root: string;
    public: string;
    temp: string;
    logs: string;
    filedb: string;
  };
  client: { name: string; event_title: string; event_year: string };
  db: {
    driver: string;
    host: string;
    database: string;
    username: string;
    password: string;
    error: string | null;
  };
  smtp: { host: string; port: string; username: string; password: string; default_email: string };
  jwt: { issuer: string; lifetime: number; private_key: string; public_key: string };
  schema: { current: number; target: number; pending: number[] };
  columns: { [name: string]: boolean | null };
}

/** Result of a manual migration run (POST /v3/config/migrate). */
export interface MigrationResult {
  from: number;
  to: number;
  applied: number[];
  ok: boolean;
  error: string | null;
  locked: boolean;
}

export interface MigrationResponse {
  status: number;
  result: MigrationResult;
}

/**
 * Talks to the superuser-only /v3/config* API. Follows the exhibitor.service
 * HttpClient pattern; the acting user id is sent both as the `uid` param and the
 * `X-User-Id` header so the server-side SuperuserAuthMiddleware can verify
 * t_beuser.su == 1 (Authorization Bearer is added by the global interceptor).
 */
@Injectable({
  providedIn: 'root',
})
export class EventConfigService {
  constructor(private httpClient: HttpClient, private authQuery: AuthQuery) {}

  /** Broadcasts a freshly-saved event phase so the dashboard header updates live. */
  private phaseChanged = new Subject<string>();
  readonly phaseChanged$ = this.phaseChanged.asObservable();

  notifyPhaseChanged(phase: string): void {
    this.phaseChanged.next(phase);
  }

  private get uid(): string {
    return '' + (this.authQuery.userid ?? '');
  }

  private headers(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'X-User-Id': this.uid,
    });
  }

  getConfig(): Observable<EventConfigResponse> {
    return this.httpClient.get<EventConfigResponse>(`${environment.serverUrl}/config`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  /**
   * Read-only current event phase, available to ALL authenticated backend
   * users (not just superusers). Backed by the non-superuser GET /v3/phase
   * endpoint; the Bearer token is added by the global HTTP interceptor.
   */
  getPhase(): Observable<{ status: number; phase: string }> {
    return this.httpClient.get<{ status: number; phase: string }>(`${environment.serverUrl}/phase`);
  }

  saveConfig(settings: { [key: string]: any }): Observable<{ status: number; saved: number }> {
    return this.httpClient.post<{ status: number; saved: number }>(
      `${environment.serverUrl}/config`,
      { uid: this.uid, settings },
      { headers: this.headers() }
    );
  }

  getDays(): Observable<{ status: number; days: EventDay[] }> {
    return this.httpClient.get<{ status: number; days: EventDay[] }>(`${environment.serverUrl}/config/days`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  saveDays(days: EventDay[]): Observable<{ status: number; saved: number }> {
    return this.httpClient.post<{ status: number; saved: number }>(
      `${environment.serverUrl}/config/days`,
      { uid: this.uid, days },
      { headers: this.headers() }
    );
  }

  getContent(): Observable<ContentResponse> {
    return this.httpClient.get<ContentResponse>(`${environment.serverUrl}/config/content`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  saveContent(blocks: SaveContentBlock[]): Observable<{ status: number; saved: number }> {
    return this.httpClient.post<{ status: number; saved: number }>(
      `${environment.serverUrl}/config/content`,
      { uid: this.uid, blocks },
      { headers: this.headers() }
    );
  }

  getEmailTemplates(): Observable<EmailTemplatesResponse> {
    return this.httpClient.get<EmailTemplatesResponse>(`${environment.serverUrl}/config/emails`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  saveEmailTemplates(templates: SaveEmailTemplate[]): Observable<{ status: number; saved: number }> {
    return this.httpClient.post<{ status: number; saved: number }>(
      `${environment.serverUrl}/config/emails`,
      { uid: this.uid, templates },
      { headers: this.headers() }
    );
  }

  /**
   * Insert built-in default templates for any (template_key, lang) that has no
   * row yet. Existing (edited) rows are preserved server-side. Used to ensure
   * every transactional email has a DB-backed template so the legacy senders
   * always use the dynamic template + brand logo instead of hardcoded fallbacks.
   */
  seedEmailTemplates(templates: SaveEmailTemplate[]): Observable<{ status: number; seeded: number }> {
    return this.httpClient.post<{ status: number; seeded: number }>(
      `${environment.serverUrl}/config/emails/seed`,
      { uid: this.uid, templates },
      { headers: this.headers() }
    );
  }

  /**
   * Upload an image embedded inside a WYSIWYG content block. Returns the path
   * relative to eplus/_fileDB so the caller can build a full URL. Used by the
   * TinyMCE images_upload_handler. Multipart, so no JSON Content-Type header.
   */
  uploadContentImage(file: Blob, filename: string): Observable<string> {
    const form = new FormData();
    form.append('uid', this.uid);
    form.append('assetkind', 'element');
    form.append('filepond', file, filename);
    return this.httpClient.post(`${environment.serverUrl}/config/asset`, form, {
      headers: new HttpHeaders({ 'X-User-Id': this.uid }),
      responseType: 'text',
    });
  }

  /**
   * List previously uploaded content images so the WYSIWYG editors can browse
   * and re-insert them. Each `path` is relative to _fileDB; build a full URL
   * with `fileDbBaseUrl`.
   */
  listContentImages(): Observable<ContentImagesResponse> {
    return this.httpClient.get<ContentImagesResponse>(`${environment.serverUrl}/config/assets`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  /** Absolute base URL where _fileDB assets are served from. */
  get fileDbBaseUrl(): string {
    return `${environment.eplusSystem}/_fileDB`;
  }

  // ---- Phase 7: per-language UI wording (event_i18n) -------------------

  getI18n(): Observable<I18nResponse> {
    return this.httpClient.get<I18nResponse>(`${environment.serverUrl}/config/i18n`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  saveI18n(entries: SaveI18nEntry[]): Observable<{ status: number; saved: number; deleted: number }> {
    return this.httpClient.post<{ status: number; saved: number; deleted: number }>(
      `${environment.serverUrl}/config/i18n`,
      { uid: this.uid, entries },
      { headers: this.headers() }
    );
  }

  // ---- Phase 6: event photo gallery ------------------------------------

  getGallery(): Observable<GalleryResponse> {
    return this.httpClient.get<GalleryResponse>(`${environment.serverUrl}/config/gallery`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  /** Upload one gallery image into a category. Multipart (FilePond). */
  uploadGalleryImage(file: Blob, filename: string, category: string): Observable<string> {
    const form = new FormData();
    form.append('uid', this.uid);
    form.append('category', category);
    form.append('filepond', file, filename);
    return this.httpClient.post(`${environment.serverUrl}/config/gallery`, form, {
      headers: new HttpHeaders({ 'X-User-Id': this.uid }),
      responseType: 'text',
    });
  }

  deleteGalleryImage(path: string): Observable<{ status: number; deleted: boolean }> {
    return this.httpClient.request<{ status: number; deleted: boolean }>(
      'delete',
      `${environment.serverUrl}/config/gallery`,
      {
        headers: this.headers(),
        body: { uid: this.uid, path },
      }
    );
  }

  /** Read the editable gallery metadata (hero, intro, category titles/cover/order). */
  getGalleryMeta(): Observable<GalleryMetaResponse> {
    return this.httpClient.get<GalleryMetaResponse>(`${environment.serverUrl}/config/gallery-meta`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  /** Persist the gallery metadata to config/event/gallery.json. */
  saveGalleryMeta(
    meta: GalleryMeta
  ): Observable<{ status: number; saved?: boolean; meta?: GalleryMeta; error?: string }> {
    return this.httpClient.post<{ status: number; saved?: boolean; meta?: GalleryMeta; error?: string }>(
      `${environment.serverUrl}/config/gallery-meta`,
      { uid: this.uid, meta },
      { headers: this.headers() }
    );
  }

  // ---- Stage 5D: raw config-file editor --------------------------------

  listConfigFiles(): Observable<{ status: number; files: ConfigFileMeta[] }> {
    return this.httpClient.get<{ status: number; files: ConfigFileMeta[] }>(
      `${environment.serverUrl}/config/files`,
      { headers: this.headers(), params: new HttpParams().set('uid', this.uid) }
    );
  }

  getConfigFile(id: string, silent = false): Observable<ConfigFileContent> {
    return this.httpClient.get<ConfigFileContent>(`${environment.serverUrl}/config/file`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid).set('id', id),
      context: new HttpContext().set(SILENT_ERROR, silent),
    });
  }

  saveConfigFile(
    id: string,
    content: string
  ): Observable<{ status: number; saved?: boolean; error?: string }> {
    return this.httpClient.post<{ status: number; saved?: boolean; error?: string }>(
      `${environment.serverUrl}/config/file`,
      { uid: this.uid, id, content },
      { headers: this.headers() }
    );
  }

  // ---- Admin user management (t_beuser, beusergroup_id = 1) -------------

  listAdminUsers(): Observable<{ status: number; users: AdminUser[] }> {
    return this.httpClient.get<{ status: number; users: AdminUser[] }>(
      `${environment.serverUrl}/config/admin-users`,
      { headers: this.headers(), params: new HttpParams().set('uid', this.uid) }
    );
  }

  saveAdminUser(
    payload: SaveAdminUserPayload
  ): Observable<{ status: number; saved: boolean; id?: number; error?: string; msg?: string }> {
    return this.httpClient.post<{ status: number; saved: boolean; id?: number; error?: string; msg?: string }>(
      `${environment.serverUrl}/config/admin-users`,
      { uid: this.uid, ...payload },
      { headers: this.headers() }
    );
  }

  deactivateAdminUser(
    id: number
  ): Observable<{ status: number; deactivated: boolean; id?: number; error?: string; msg?: string }> {
    return this.httpClient.request<{ status: number; deactivated: boolean; id?: number; error?: string; msg?: string }>(
      'delete',
      `${environment.serverUrl}/config/admin-users`,
      {
        headers: this.headers(),
        body: { uid: this.uid, id },
      }
    );
  }

  /** Upload an admin's profile image (FilePond). Multipart, no JSON header. */
  uploadAdminAvatar(file: Blob, filename: string, id: number): Observable<string> {
    const form = new FormData();
    form.append('uid', this.uid);
    form.append('id', '' + id);
    form.append('filepond', file, filename);
    return this.httpClient.post(`${environment.serverUrl}/config/admin-avatar`, form, {
      headers: new HttpHeaders({ 'X-User-Id': this.uid }),
      responseType: 'text',
    });
  }

  /** Absolute base URL where admin profile images are served from. */
  get adminAvatarBaseUrl(): string {
    return `${environment.eplusSystem}/_fileDB/images/admin`;
  }

  // ---- Sysadmin tab: system info + manual DB migration -----------------

  getSysInfo(): Observable<SysInfoResponse> {
    return this.httpClient.get<SysInfoResponse>(`${environment.serverUrl}/config/sysinfo`, {
      headers: this.headers(),
      params: new HttpParams().set('uid', this.uid),
    });
  }

  runMigration(): Observable<MigrationResponse> {
    return this.httpClient.post<MigrationResponse>(
      `${environment.serverUrl}/config/migrate`,
      { uid: this.uid },
      { headers: this.headers() }
    );
  }
}
